import React from 'react';

import moment, { Moment } from 'moment';
import MomentUtils from '@date-io/moment';
import { Field, FieldConfig } from 'formik';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core';
import { DatePicker, DatePickerProps, MuiPickersUtilsProvider, TimePicker } from 'material-ui-pickers';
import { TimePickerModalProps } from 'material-ui-pickers/TimePicker/TimePickerModal';
import { Info as InfoIcon } from '@material-ui/icons';

import Tooltip from './Tooltip';
import SvgIconWrapper from './SvgIconWrapper';

export interface FieldDateTimePickerProps extends WithStyles<typeof styles>, FieldConfig {
  datePickerProps?: Partial<DatePickerProps>;
  timePickerProps?: Partial<TimePickerModalProps>;
  disabled?: boolean;
  tooltipText?: string;
}

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    timePicker: {
      display: 'flex',
      gap: `${unit}px`,
      alignItems: 'center',
    },
  });

const FieldDateTimePicker: React.FC<FieldDateTimePickerProps> = ({
  datePickerProps,
  timePickerProps,
  disabled,
  classes,
  tooltipText,
  ...rest
}) => (
  <Field {...rest}>
    {({ field, form }: any) => {
      const errorText = form.errors[field.name];
      return (
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <DatePicker
            data-lh-id={`date-picker-${field.name}`}
            format="L"
            animateYearScrolling={false}
            {...field}
            {...datePickerProps}
            onChange={(date: Moment) =>
              form.setFieldValue(
                field.name,
                moment(`${date.format('YYYY-MM-DD')} ${moment(field.value).format('HH:mm')}`),
              )
            }
            // https://github.com/react-dnd/react-dnd/issues/701
            onDrop={(e) => e.preventDefault()}
            disabled={disabled}
          />
          <div className={classes.timePicker}>
            <TimePicker
              data-lh-id={`time-picker-${field.name}`}
              autoComplete="nope"
              {...field}
              {...timePickerProps}
              error={!disabled && !!errorText}
              helperText={!disabled && errorText}
              onChange={(date: Moment) =>
                form.setFieldValue(
                  field.name,
                  moment(`${moment(field.value).format('YYYY-MM-DD')} ${date.format('HH:mm')}`),
                )
              }
              // https://github.com/react-dnd/react-dnd/issues/701
              onDrop={(e) => e.preventDefault()}
              disabled={disabled}
            />
            {tooltipText && (
              <Tooltip title={tooltipText} placement="top">
                <SvgIconWrapper Icon={InfoIcon} color="supportSlate" fontSize="small" />
              </Tooltip>
            )}
          </div>
        </MuiPickersUtilsProvider>
      );
    }}
  </Field>
);

export default withStyles(styles)(FieldDateTimePicker);
